// frame.js
// Secure preview URLs are short lived and may be embedded only by the
// configured RueBaRue admin origins.
import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getFullHeightPreviewDimensions } from "./dimensions";

const DEVICES = {
  desktop: { label: "Desktop", w: 1024, h: 768 },
  tablet: { label: "Tablet", w: 768, h: 1024 },
  mobile: { label: "Mobile", w: 430, h: 860 },
};

const POPUP_NAME = "rbr_guide_preview";

const GuidePreviewFrame = forwardRef(({ src, emptyMessage }, ref) => {
  const popupRef = useRef(null);
  const canvasRef = useRef(null);
  const [device, setDevice] = useState("desktop");
  const [popupOpen, setPopupOpen] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [refreshToken, setRefreshToken] = useState(0);

  const activeDevice = DEVICES[device];
  const inlinePreviewEnabled = useMemo(() => {
    if (!src) return false;
    try {
      const hostname = new URL(src).hostname;
      return (
        process.env.REACT_APP_EMBED_GUIDE_PREVIEW === "true" ||
        hostname === "localhost" ||
        hostname === "127.0.0.1"
      );
    } catch {
      return false;
    }
  }, [src]);

  const { scale: previewScale, scaledWidth, scaledHeight, viewportHeight } =
    useMemo(
      () =>
        getFullHeightPreviewDimensions({
          device: activeDevice,
          canvasWidth: canvasSize.width,
          canvasHeight: canvasSize.height,
        }),
      [activeDevice, canvasSize.height, canvasSize.width],
    );

  const buildUrl = () =>
    src ? `${src}${src.includes("?") ? "&" : "?"}_=${Date.now()}` : "";

  const buildInlinePreviewUrl = () => {
    if (!src) {
      return "";
    }

    try {
      const url = new URL(src);
      url.searchParams.set("_", String(refreshToken));
      return url.toString();
    } catch {
      return "";
    }
  };

  const openPopup = () => {
    if (!src) return;
    const { w, h } = DEVICES[device];
    const left = Math.max(0, (window.screen.availWidth - w) / 2);
    const top = Math.max(0, (window.screen.availHeight - h) / 2);
    const features = `popup=yes,width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`;
    const win = window.open(buildUrl(), POPUP_NAME, features);
    if (win) {
      popupRef.current = win;
      setPopupOpen(true);
      win.focus();
    }
  };

  const refreshPopup = () => {
    const win = popupRef.current;
    if (!win || win.closed) {
      openPopup();
      return;
    }
    try {
      // Cross-origin: we cannot read the document, but assigning
      // `location.href` to a same-named popup is allowed and
      // forces a full reload with cache-busting.
      win.location.href = buildUrl();
      win.focus();
    } catch {
      // As a last resort, re-open with a fresh URL.
      openPopup();
    }
  };

  const refreshInlinePreview = () => {
    setRefreshToken((current) => current + 1);
  };

  // Poll to detect manual close, so the UI stays in sync.
  useEffect(() => {
    if (!popupOpen) return;
    const id = window.setInterval(() => {
      if (!popupRef.current || popupRef.current.closed) {
        popupRef.current = null;
        setPopupOpen(false);
        window.clearInterval(id);
      }
    }, 750);
    return () => window.clearInterval(id);
  }, [popupOpen]);

  // Close the popup when the builder unmounts.
  useEffect(
    () => () => {
      try {
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      } catch {
        /* ignore */
      }
    },
    [],
  );

  // When the device size changes and the popup is already open,
  // resize it to match.
  useEffect(() => {
    const win = popupRef.current;
    if (win && !win.closed) {
      const { w, h } = DEVICES[device];
      try {
        win.resizeTo(w, h);
      } catch {
        /* some browsers block resizeTo; safe to ignore */
      }
    }
  }, [device]);

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    let resizeFrameId = null;
    let nextCanvasSize = null;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      nextCanvasSize = {
        width: Math.round(width),
        height: Math.round(height),
      };

      if (resizeFrameId !== null) {
        return;
      }

      // ResizeObserver callbacks run during layout. Updating React state in
      // that same cycle can resize the preview again and trigger the browser's
      // "undelivered notifications" loop warning while changing sections.
      resizeFrameId = window.requestAnimationFrame(() => {
        resizeFrameId = null;
        const measuredSize = nextCanvasSize;
        nextCanvasSize = null;
        setCanvasSize((currentSize) =>
          currentSize.width === measuredSize.width &&
          currentSize.height === measuredSize.height
            ? currentSize
            : measuredSize,
        );
      });
    });

    observer.observe(canvasEl);
    return () => {
      observer.disconnect();
      if (resizeFrameId !== null) {
        window.cancelAnimationFrame(resizeFrameId);
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: refreshInlinePreview,
    open: openPopup,
  }));

  return (
    <div className="guide-preview-shell">
      <div className="guide-preview-toolbar">
        <div className="guide-preview-device-toggle">
          {Object.entries(DEVICES).map(([key, def]) => (
            <button
              key={key}
              type="button"
              className={`guide-preview-device-btn ${
                device === key ? "is-active" : ""
              }`}
              onClick={() => setDevice(key)}
            >
              {def.label}
            </button>
          ))}
        </div>
        <div className="guide-preview-actions">
          {src && (
            <a
              className="uk-button hms-btn hms-white-btn"
              href={src}
              target="_blank"
              rel="noreferrer"
            >
              <span data-uk-icon="icon: link; ratio: 0.8" />
              <span style={{ marginLeft: 6 }}>New Tab</span>
            </a>
          )}
          <button
            type="button"
            className="uk-button hms-btn hms-white-btn"
            onClick={popupOpen ? refreshPopup : openPopup}
            disabled={!src}
          >
            <span
              data-uk-icon={`icon: ${popupOpen ? "refresh" : "play"}; ratio: 0.8`}
            />
            <span style={{ marginLeft: 6 }}>
              {popupOpen ? "Refresh Preview" : "Open Preview"}
            </span>
          </button>
        </div>
      </div>

      <div className="guide-preview-canvas" ref={canvasRef}>
        <div className="guide-preview-empty-state">
          {!src ? (
            <div className="guide-preview-empty">
              <p>{emptyMessage || "Select a property to preview the guide."}</p>
            </div>
          ) : !inlinePreviewEnabled ? (
            <div className="guide-preview-empty">
              <span data-uk-icon="icon: window; ratio: 2" />
              <h4 className="uk-margin-small-top uk-margin-remove-bottom">
                Secure preview is ready
              </h4>
              <p className="uk-text-meta uk-margin-small-top">
                Open the signed preview using the selected device size.
              </p>
              <button
                type="button"
                className="uk-button hms-btn hms-primary-btn"
                onClick={openPopup}
              >
                Open Preview
              </button>
            </div>
          ) : (
            <div
              className={`guide-preview-stage guide-preview-stage--${device}`}
              style={{ width: `${scaledWidth}px`, height: `${scaledHeight}px` }}
            >
              <div
                className={`guide-preview-device guide-preview-device--${device}`}
                style={{
                  width: `${activeDevice.w}px`,
                  height: `${viewportHeight}px`,
                  transform: `scale(${previewScale})`,
                }}
              >
                <iframe
                  title={`${activeDevice.label} guide preview`}
                  src={buildInlinePreviewUrl()}
                  allow="microphone; cross-origin-isolated"
                />
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
});

export default GuidePreviewFrame;
